import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Radio } from "@mui/material";
import { StrucRow } from "../StructureElements/StrucRow";
import { StrucCol } from "../StructureElements/StrucCol";
import { InputWrapper } from "../Input/InputWrapper";
import { InputLabel } from "../Input/InputLabel";
import { CustomTextArea } from "../Input/CustomInput";
import { SecondaryButton, WarnButton } from "../Buttons/SecondaryButton";
import { Dialog_VM, DialogFrame, useDialog } from "./dialog-base";
import LoadingElement from "../loading-element/loading-element";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { Role } from "../../../core/models/entities/Role";
import { useErrorToast } from "../Toast/Toast";

interface DialogCont {
  component: JSX.Element | null;
  open: () => void;
}

type Option = "delete" | "note";

export function useRejectLogoDialog(
  title: string,
  onReject: (reason?: string) => void,
  onDelete: (reason?: string) => void,
  onClose: () => void,
  isForOrders?: boolean
): DialogCont {
  const { t } = useTranslation();
  const [rejectionNote, setRejectionNote] = useState<string>("");
  const dialog = useDialog();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [option, setOption] = useState<Option>("note");

  function handleReject() {
    // Validate that rejection note is provided when "note" option is selected
    if (option === "note" && !rejectionNote.trim()) {
      useErrorToast(t("compDialogsInput.rejectionNoteRequired"));
      return;
    }

    if (option === "note") {
      onReject(rejectionNote);
    } else {
      onDelete(rejectionNote);
    }
    dialog.closeDialog();
  }

  function handleCancel() {
    onClose();
    dialog.closeDialog();
  }

  function handleOption(option: Option) {
    setOption(option);
  }

  return {
    component: isLoading ? null : (
      <RejectLogoDialog
        option={option}
        setOption={handleOption}
        isLoading={isLoading}
        dialogVm={dialog}
        setReason={setRejectionNote}
        onReject={handleReject}
        onClose={handleCancel}
        title={title}
        isForOrders={isForOrders}
      />
    ),
    open: dialog.openDialog,
  };
}

interface Props {
  option: Option;
  setOption: (option: Option) => void;
  setReason: (reason: string) => void;
  onReject: () => void;
  onClose: () => void;
  title: string;
  dialogVm: Dialog_VM;
  isLoading: boolean;
  isForOrders?: boolean;
}

export function RejectLogoDialog({
  option,
  setOption,
  isLoading,
  setReason,
  onReject,
  onClose,
  title,
  dialogVm,
  isForOrders,
}: Props) {
  const { t } = useTranslation();
  const { profile } = useProfileContext();
  const isCustomer =
    profile?.userOrganizationInformations.role === Role.EndUser;
  if (isLoading) return null;

  return (
    <DialogFrame onClose={onClose} dialogOption={dialogVm}>
      {isLoading && <LoadingElement transparent />}
      <span>{title}</span>
      {!isForOrders && (
        <div className={"flex flex-col ml-[-8px]"}>
          {!isCustomer && (
            <div>
              <Radio
                checked={option === "delete"}
                onChange={() => setOption("delete")}
                value="delete"
                name="radio-buttons"
                inputProps={{ "aria-label": t("delete") }}
              />
              <span>{t("delete")}</span>
            </div>
          )}
          <div>
            {!isCustomer && (
              <Radio
                checked={option === "note"}
                onChange={() => setOption("note")}
                value="note"
                name="radio-buttons"
                inputProps={{ "aria-label": t("compDialogsInput.note") }}
              />
            )}
            <span>{t("compDialogsInput.rejectWithRemark")}</span>
          </div>
        </div>
      )}
      <StrucRow className="mt-2">
        <StrucCol>
          <InputWrapper>
            <InputLabel>
              <span>{t("compDialogsInput.rejectionNote")}</span>
            </InputLabel>
            <CustomTextArea
              disabled={option !== "note"}
              onChange={(e) => setReason(e.target.value)}
            />
          </InputWrapper>
        </StrucCol>
      </StrucRow>
      <div className={"flex justify-between space-x-4"}>
        <WarnButton width={"full"} onClick={onReject}>
          {option === "delete" ? t("delete") : t("compDialogsInput.reject")}
        </WarnButton>
        <SecondaryButton width={"full"} onClick={onClose}>
          {t("cancel")}
        </SecondaryButton>
      </div>
    </DialogFrame>
  );
}
